import { useState, useEffect } from "react";

export type Orientation = "portrait" | "landscape";

/**
 * Tracks the device orientation and updates live when the user rotates
 * their phone. Falls back gracefully on desktops (always "portrait").
 */
export function useOrientation(): Orientation {
  const get = (): Orientation =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(orientation: landscape)").matches
        ? "landscape"
        : "portrait"
      : "portrait";

  const [orientation, setOrientation] = useState<Orientation>(get);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(orientation: landscape)");
    const handler = (e: MediaQueryListEvent) =>
      setOrientation(e.matches ? "landscape" : "portrait");
    mql.addEventListener("change", handler);
    // Also listen to resize as a fallback for older browsers / WebView quirks
    const onResize = () => setOrientation(get());
    window.addEventListener("resize", onResize);
    return () => {
      mql.removeEventListener("change", handler);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return orientation;
}
